import {
  takeLatest, call, put, select,
} from 'redux-saga/effects';
import _ from 'lodash';
import axios from '../axiosConfig';
import {
  carSearch, carSuccess, carFailure, updateCarSearchObj,
} from '../../slices/car';
import uniqKeyGenerator from '../../../utils/uniqKeyGenerator';
import { SearchStatus, LocationTypes, TravelTypes } from '../../../constants';
import carSearchInput from '../../../helpers/selectors/carSearchInput';
import {
  searchFail, searchSuccess, setCarTripSearchParam, setTripSearchProperty,
} from '../../slices/tripSearch';
import { changeTrip } from '../../slices/trip';
import checkDoShowMarkupRates from '../../../helpers/selectors/checkDoShowMarkupRates';

const queryString = require('query-string');

function* rentalCarsSearchWorker({ payload }) {
  try {
    const { rentalCarDiscountCodes, travelAgency } = yield select((store) => store.user);
    const { currencyCode } = yield select((store) => store.tripSearch);
    const { ignoreCachedResultsForModes } = yield select((store) => store.trip);
    const {
      staterooms,
    } = yield select((store) => store.tripSearch.searchObj);
    const {
      searchObj,
    } = yield select(carSearchInput(payload.tripId, payload.selectedTab, payload.isTripSearch));
    const doConsiderMarkupRate = yield select(checkDoShowMarkupRates({
      mode: TravelTypes.carrental, tripId: payload.tripId, itineraryId: payload.selectedTab,
    }));

    let rateCodes = (payload && payload.searchObj
      && payload.searchObj.rateCodes) || (searchObj && searchObj.rateCodes);
    let rentalCarAgencies = (payload && payload.searchObj
        && payload.searchObj.rentalCarAgencies) || (searchObj && searchObj.rentalCarAgencies);

    const transmissionTypes = (payload && payload.searchObj
      && payload.searchObj.transmissionTypes)
      || (searchObj && searchObj.transmissionTypes);

    if (rentalCarDiscountCodes && rentalCarDiscountCodes.length && rateCodes === undefined) {
      const filteredRateCodes = _.filter(rentalCarDiscountCodes, (r) => r.isSelectedByDefault);
      rateCodes = _.map(filteredRateCodes, 'id');
      if (!(rentalCarAgencies || []).length && travelAgency
      && travelAgency.defaultSetRateCodeCarVendorAsFilter) {
        rentalCarAgencies = _.uniq(_.map(filteredRateCodes, 'vendorCode'));
      }
      if (rateCodes.length === 0) {
        rateCodes = undefined;
        rentalCarAgencies = undefined;
      }
    }

    const {
      minBudget,
      maxBudget,
      carTabs,
    } = yield select((store) => (store.car.carSearches && store.car.carSearches[payload.tripId])
      || {});
    const { pickupCode, dropoffCode } = searchObj;

    let fromLocationType = 'fromPlaceId';
    if (pickupCode.airportCode) fromLocationType = 'fromAirportCode';
    if (pickupCode.locationType === LocationTypes.PORT_OF_DEPARTURE) fromLocationType = 'fromCruiseDepartureCityId';

    let toLocationType = 'toPlaceId';
    if (dropoffCode.airportCode) toLocationType = 'toAirportCode';
    if (dropoffCode.locationType === LocationTypes.PORT_OF_DEPARTURE) toLocationType = 'toCruiseDepartureCityId';
    const doIgnoreCachedResults = ignoreCachedResultsForModes && ignoreCachedResultsForModes.length
    && ignoreCachedResultsForModes.indexOf(TravelTypes.carrental) > -1
      ? true : undefined;

    const queryObj = {
      ...searchObj,
      childrenAge: _.flatMap(staterooms, (st) => Object.values(st.children || [])),
      seatInfants: _.sumBy(staterooms, (st) => st.seatInfants || 0),
      adults: _.sumBy(staterooms, (st) => st.adults || 0)
        + _.sumBy(staterooms, (st) => st.seniors || 0),
      [fromLocationType]: pickupCode.airportCode ? pickupCode.airportCode : pickupCode.value,
      [toLocationType]: dropoffCode.airportCode ? dropoffCode.airportCode : dropoffCode.value,
      doReturnTravelOptions: payload.isTripSearch,
      rateCodes,
      rentalCarAgencies,
      doIgnoreCachedResults,
      currencyCode,
      doConsiderMarkupRate,
      transmissionTypes,
    };

    if (!queryObj[fromLocationType] && pickupCode.latitude) {
      queryObj.fromLatitude = pickupCode.latitude;
      queryObj.fromLongitude = pickupCode.longitude;
    }
    if (!queryObj[toLocationType] && dropoffCode.latitude) {
      queryObj.toLatitude = dropoffCode.latitude;
      queryObj.toLongitude = dropoffCode.longitude;
    }
    delete queryObj.carSearchType;
    delete queryObj.pickupCode;
    delete queryObj.dropoffCode;
    delete queryObj.responseFilters;
    delete queryObj.defaultSearchObj;

    const qs = queryString.stringify(queryObj, { skipNull: true });

    const responses = yield select((store) => store.car.responses);
    let responseData = responses[qs];
    if (!responseData || doIgnoreCachedResults) {
      if (doIgnoreCachedResults) yield put(changeTrip({ field: 'ignoreCachedResultsForModes', value: _.reject(ignoreCachedResultsForModes, (ss) => ss === TravelTypes.carrental) }));
      if (payload.isTripSearch) {
        yield put(setTripSearchProperty({ searchStatus: SearchStatus.SEARCHING }));
      }
      const url = `/rentalcars/search?${qs}`;
      const config = {
        method: 'get',
        url,
      };
      const { data } = yield call(axios, config);
      if (data.legs && Array.isArray(data.legs)) {
        uniqKeyGenerator(data.legs);
      }
      responseData = data;
    }

    const defaultSearchObj = {
      fromBudget: responseData.minBudget,
      toBudget: responseData.maxBudget,
    };
    const updatedSearchObj = {
      ...searchObj,
      rateCodes,
      fromBudget: searchObj.fromBudget
        && minBudget === responseData.minBudget
        ? searchObj.fromBudget : responseData.minBudget,
      toBudget: searchObj.toBudget
        && maxBudget === responseData.maxBudget
        ? searchObj.toBudget : responseData.maxBudget,
    };

    if (responseData && responseData.filters
      && responseData.filters.carRentalAgencies) {
      updatedSearchObj.rentalCarAgencies = _.map(_.filter(responseData.filters.carRentalAgencies, (f) => f.enabled), 'code');
    }
    const updatedFilters = {
      minBudget: responseData && responseData.filters && responseData.filters.minBudget,
      maxBudget: responseData && responseData.filters && responseData.filters.maxBudget,
      rentalAgencyResults: responseData && responseData.filters
        && responseData.filters.carRentalAgencies ? responseData.filters
          .carRentalAgencies
          .map((item) => ({
            value: item.code,
            label: item.name,
          })) : [],
      carTypesResults: responseData && responseData.filters
        && responseData.filters.carTypes ? responseData.filters
          .carTypes
          .map((item) => ({
            value: item.name,
            label: item.name,
            minPrice: item.minPrice,
          })) : [],
    };
    if (payload.isTripSearch) {
      yield put(setCarTripSearchParam({
        defaultSearchObj,
        ...updatedSearchObj,
        responseFilters: {
          ...responseData.filters,
          ...updatedFilters,
        },
      }));
      yield put(searchSuccess(responseData));
    } else {
      yield put(updateCarSearchObj({
        tripId: payload.tripId,
        defaultSearchObj,
        carTabs: {
          ...carTabs,
          [payload.selectedTab]: {
            ...carTabs[payload.selectedTab],
            searchObj: updatedSearchObj,
          },
        },
        searchStatus: SearchStatus.DONE,
      }));
      yield put(carSuccess({
        queryString: qs,
        tab: payload.selectedTab,
        response: responseData,
        tripId: payload.tripId,
        ...updatedFilters,
      }));
    }
  } catch (error) {
    let errorMsg = 'Could not find cars';
    if (payload.isTripSearch) {
      yield put(searchFail(errorMsg));
    } else {
      if (error.response && error.response.data && error.response.data.title) {
        errorMsg = `${error.response.data.title} ${error.response.data.detail}`;
      }
      yield put(carFailure({
        error: errorMsg,
        tab: payload.selectedTab,
        tripId: payload.tripId,
      }));
    }
  }
}

function* rentalCarsSearchWatcher() {
  yield takeLatest(carSearch.toString(), rentalCarsSearchWorker);
}

export default rentalCarsSearchWatcher;
